import { useCallback } from 'react';
import { navigate } from '@reach/router';
import { LoginMethod, UrlParam } from '@/constants';
import {
    pathToHome,
    pathToLogin,
    pathToNextAccountLogin,
    pathToSSOCallback,
    pathToSSOLogin,
    Query,
} from '@/helpers/route-helpers';
import { useLocationSearch } from '@/hooks/useLocationSearch';
import { ssoParamsRepository } from '@/infra/repository/SSOParamsRepository';
import { AuthParams } from '@/models/domain/AuthParams';
import { SSOParams } from '@/models/domain/SSOParams';

const Paths = {
    SelectLogin: pathToLogin,
    NextAccountLogin: pathToNextAccountLogin,
    SSOLogin: pathToSSOLogin,
    SSOCallback: pathToSSOCallback,
    Home: () => pathToHome(),
};

/**
 * ログイン後のページ遷移やパラメータありURLの遷移処理を行う
 */
export const useRouter = () => {
    const {
        cloudId,
        loginMethod,
        loginId,
        origin,
        select,
        close,
        error: ssoError,
        errorDescription,
    } = useLocationSearch();

    const createQuery = useCallback(
        (authParams?: AuthParams, forceSelect?: boolean, ssoParams?: SSOParams) => {
            // ssoParamsはSSOのエラー時にoriginやerrorをログインまで送るために使用する
            const query: Query = {
                [UrlParam.CLOUD_ID]: authParams?.cloudId?.value || cloudId,
                [UrlParam.LOGIN_METHOD]: authParams?.loginMethod || loginMethod,
                [UrlParam.LOGIN_ID]: loginId,
                [UrlParam.ORIGIN]: ssoParams?.origin || origin,
                [UrlParam.SELECT]: forceSelect || select ? 'true' : undefined,
                [UrlParam.CLOSE]: close ? 'true' : undefined,
                [UrlParam.ERROR]: ssoParams ? ssoError : undefined, // ログインまで送った後は不要になるため
                [UrlParam.ERROR_DESCRIPTION]: ssoParams ? errorDescription : undefined,
            };

            return query;
        },
        [cloudId, loginMethod, loginId, origin, select, close, ssoError, errorDescription],
    );

    const getValidUrl = useCallback((inputOrigin?: string) => {
        try {
            if (!inputOrigin) {
                return undefined;
            }

            const decodedUrl = decodeURIComponent(inputOrigin);
            const originUrl = new URL(decodedUrl, window.location.href);
            if (originUrl.hostname !== window.location.hostname) {
                return undefined;
            }
            return originUrl.href;
        } catch (error) {
            // 有効で無いURLは無視する
            return undefined;
        }
    }, []);

    const toForceSelectLoginPagePath = useCallback(
        (authParams?: AuthParams) => {
            return Paths.SelectLogin(createQuery(authParams, true));
        },
        [createQuery],
    );

    const navigateToLoginPage = useCallback(
        (authParams?: AuthParams) => {
            if (!authParams) {
                return;
            }

            const query = createQuery(authParams);

            if (authParams.loginMethod === LoginMethod.NEXT_ACCOUNT) {
                // navigate(Paths.NextAccountLogin(...paramInfos));
                navigate(Paths.NextAccountLogin(query));
            } else if (authParams.loginMethod === LoginMethod.SSO) {
                navigate(Paths.SSOLogin(query));
            }
        },
        [createQuery],
    );

    const navigateToSelectLoginMethodPage = useCallback(
        (authParams?: AuthParams) => {
            navigate(Paths.SelectLogin(createQuery(authParams, true)));
        },
        [createQuery],
    );

    const navigateToSelectLoginMethodPageForSSOError = useCallback(
        (authParams?: AuthParams) => {
            const ssoParams = ssoParamsRepository.get();
            navigate(Paths.SelectLogin(createQuery(authParams, true, ssoParams)));
        },
        [createQuery],
    );

    const navigateToSelectLoginMethodPageForSSOLambdaError = useCallback(
        (authParams?: AuthParams, lambdaError?: string) => {
            const ssoParams = ssoParamsRepository.get();
            navigate(Paths.SelectLogin({ ...createQuery(authParams, true, ssoParams), lambdaError }));
        },
        [createQuery],
    );

    const navigateToNextAccountLoginPage = useCallback(
        (authParams?: AuthParams) => {
            navigate(Paths.NextAccountLogin(createQuery(authParams)));
        },
        [createQuery],
    );

    const navigateToSSOLoginPage = useCallback(
        (authParams?: AuthParams) => {
            navigate(Paths.SSOLogin(createQuery(authParams)));
        },
        [createQuery],
    );

    const movePageAfterSuccessfulLogin = useCallback(() => {
        if (close) {
            window.close();
        }

        window.location.href = getValidUrl(origin) ?? Paths.Home();
    }, [close, getValidUrl, origin]);

    const movePageAfterSuccessfulSSOLogin = useCallback(() => {
        const ssoParams = ssoParamsRepository.get();
        if (ssoParams.close) {
            window.close();
        }

        window.location.href = getValidUrl(ssoParams.origin) ?? Paths.Home();
    }, [getValidUrl]);

    return {
        Paths,
        toForceSelectLoginPagePath,
        navigateToLoginPage,
        navigateToSelectLoginMethodPage,
        navigateToSelectLoginMethodPageForSSOError,
        navigateToSelectLoginMethodPageForSSOLambdaError,
        navigateToNextAccountLoginPage,
        navigateToSSOLoginPage,
        movePageAfterSuccessfulLogin,
        movePageAfterSuccessfulSSOLogin,
    };
};
